import { Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { ButtonLink } from '~portal/components/common';
import type { ExploreCard } from '~portal/data';

/** @desc Figma's CTA wording per card (`426:7806`, H9). */
export const EXPLORE_CTA: Record<ExploreCard['key'], string> = {
  agent: 'Go to H2GC Agent',
  operators: 'Go to Operator view',
  regions: 'Go to Region view',
  reports: 'Go to Reports',
};

/** @desc Each card's call to action: a full-width filled Accent pill. The agent opens a fresh conversation, as the nav's Chat does. */
function ExploreButton({ card }: { card: ExploreCard }) {
  const label = EXPLORE_CTA[card.key];
  switch (card.key) {
    case 'agent':
      return (
        <ButtonLink fullWidth to="/chat/$conversationId" params={{ conversationId: 'new' }}>
          {label}
        </ButtonLink>
      );
    case 'operators':
      return (
        <ButtonLink fullWidth to="/operators">
          {label}
        </ButtonLink>
      );
    case 'regions':
      return (
        <ButtonLink fullWidth to="/regions">
          {label}
        </ButtonLink>
      );
    case 'reports':
      return (
        <ButtonLink fullWidth to="/reports">
          {label}
        </ButtonLink>
      );
  }
}

/**
 * @desc Explore (H9, Figma `426:7806`): "Explore H2GC's platform", four cards with a serif card
 * title, no icon, a line of description and a full-width Accent pill. The pill takes the hover,
 * not the card (client decision: hover on buttons and links, not cards).
 */
export function ExplorePlatform({ cards }: { cards: readonly ExploreCard[] }) {
  return (
    <Stack gap="sm" component="section" aria-labelledby="home-explore">
      <Title order={2} id="home-explore">
        Explore H2GC&rsquo;s platform
      </Title>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
        {cards.map((card) => (
          <Paper key={card.key} withBorder p="lg" component="article" aria-label={card.title}>
            <Stack gap="sm" h="100%">
              <Title order={3}>{card.title}</Title>
              <Text size="sm" c="dimmed" flex={1}>
                {card.description}
              </Text>
              <ExploreButton card={card} />
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
